import { Box, Skeleton, Typography } from "@mui/material";
import { OverviewCleaningStats } from "../models/types";
import { microLabelSx } from "../styles";

interface Props {
	cleaning: OverviewCleaningStats[];
	loading?: boolean;
}

const CleaningCycleSkeleton = () => (
	<>
		<Box sx={{ display: "flex", gap: 1.25, mb: 0.5 }}>
			<Skeleton variant="text" width={110} height={20} />
			<Skeleton variant="text" width={70} height={20} />
			<Skeleton variant="text" width={70} height={20} />
		</Box>
		{Array.from({ length: 3 }).map((_, i) => (
			<Box
				key={i}
				sx={{
					display: "grid",
					gridTemplateColumns: GRID_COLUMNS,
					columnGap: 0.5,
					py: 0.2,
					px: 0.5,
				}}
			>
				<Skeleton variant="text" width={40} />
				<Skeleton variant="text" />
				<Box />
				<Skeleton variant="text" width={40} />
				<Skeleton variant="text" />
			</Box>
		))}
	</>
);

const fmtSec = (n: number | null | undefined) =>
	n == null ? "-" : `${n.toFixed(1)}s`;

const SUSPICIOUS_SHORT_RATIO = 0.4;
const OK_OVER_RATIO = 1.2;
const WARN_OVER_RATIO = 1.5;

const lastSecColor = (last: number | null, target: number | null) => {
	if (last == null) return "#bbb";
	if (target == null || target <= 0) return "#222";
	const ratio = last / target;
	if (ratio < SUSPICIOUS_SHORT_RATIO) return "#1976d2";
	if (ratio <= OK_OVER_RATIO) return "#2e7d32";
	if (ratio <= WARN_OVER_RATIO) return "#f57c00";
	return "#d32f2f";
};

const robotLabel = (r: OverviewCleaningStats): string => {
	if (r.DisplayName) return r.DisplayName;
	if (r.Side && r.PositionIndex != null) return `${r.Side}${r.PositionIndex}`;
	return r.RobotCode;
};

// West labels | West last | gutter | East labels | East last
const GRID_COLUMNS = "auto 1fr 16px auto 1fr";

const RobotCells = ({ r }: { r: OverviewCleaningStats | null }) => {
	if (!r)
		return (
			<>
				<Box />
				<Box />
			</>
		);
	return (
		<>
			<Typography
				sx={{
					color: "#666",
					fontSize: "0.75rem",
					fontWeight: 600,
					whiteSpace: "nowrap",
				}}
			>
				{robotLabel(r)}
			</Typography>
			<Typography
				sx={{
					fontSize: "0.85rem",
					fontWeight: 700,
					textAlign: "right",
					color: lastSecColor(r.LastSec, r.TargetSec),
					fontVariantNumeric: "tabular-nums",
				}}
			>
				{fmtSec(r.LastSec)}
			</Typography>
		</>
	);
};

export const CleaningCycleCard = ({ cleaning, loading }: Props) => {
	if (loading) return <CleaningCycleSkeleton />;
	const targets = cleaning
		.map((c) => c.TargetSec)
		.filter((t): t is number => t != null);
	const target = targets.length > 0 ? Math.max(...targets) : null;
	const windowH = cleaning[0]?.WindowHours ?? 24;

	const byPos = (a: OverviewCleaningStats, b: OverviewCleaningStats) =>
		(a.PositionIndex ?? 0) - (b.PositionIndex ?? 0);
	const west = [
		...cleaning.filter((c) => c.Side === "W"),
		...cleaning.filter((c) => c.Side == null),
	].sort(byPos);
	const east = cleaning.filter((c) => c.Side === "E").sort(byPos);
	const rowCount = Math.max(west.length, east.length);
	const rows = Array.from({ length: rowCount }, (_, i) => ({
		west: west[i] ?? null,
		east: east[i] ?? null,
	}));

	return (
		<>
			<Box
				sx={{
					display: "flex",
					alignItems: "baseline",
					gap: 1.25,
					mb: 0.5,
					flexWrap: "wrap",
					rowGap: 0.25,
				}}
			>
				<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
					Cleaning cycles
				</Typography>
				<Box
					component="span"
					sx={{
						display: "inline-flex",
						alignItems: "baseline",
						gap: 0.4,
					}}
				>
					<Box component="span" sx={microLabelSx}>
						Target
					</Box>
					<Box
						component="span"
						sx={{
							fontSize: "0.8rem",
							color: target == null ? "#bbb" : "#333",
							fontWeight: 700,
							fontVariantNumeric: "tabular-nums",
						}}
					>
						{fmtSec(target)}
					</Box>
				</Box>
				<Box
					component="span"
					sx={{
						display: "inline-flex",
						alignItems: "baseline",
						gap: 0.4,
					}}
				>
					<Box component="span" sx={microLabelSx}>
						Window
					</Box>
					<Box
						component="span"
						sx={{
							fontSize: "0.8rem",
							color: "#333",
							fontWeight: 700,
							fontVariantNumeric: "tabular-nums",
						}}
					>
						{windowH}h
					</Box>
				</Box>
			</Box>

			{cleaning.length === 0 ? (
				<Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
					No cleaning data yet.
				</Typography>
			) : (
				<Box>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: GRID_COLUMNS,
							alignItems: "baseline",
							columnGap: 0.5,
							py: 0.3,
							px: 0.5,
							borderBottom: "1px solid #e0e0e0",
						}}
					>
						<Typography sx={microLabelSx}>Robot</Typography>
						<Typography sx={{ ...microLabelSx, textAlign: "right" }}>
							Last
						</Typography>
						<Box />
						<Typography sx={microLabelSx}>Robot</Typography>
						<Typography sx={{ ...microLabelSx, textAlign: "right" }}>
							Last
						</Typography>
					</Box>

					{rows.map((row, i) => (
						<Box
							key={row.west?.RobotId ?? row.east?.RobotId ?? i}
							sx={{
								display: "grid",
								gridTemplateColumns: GRID_COLUMNS,
								alignItems: "center",
								columnGap: 0.5,
								py: 0.25,
								px: 0.5,
								"&:hover": { backgroundColor: "#fafafa" },
							}}
						>
							<RobotCells r={row.west} />
							<Box />
							<RobotCells r={row.east} />
						</Box>
					))}
				</Box>
			)}
		</>
	);
};
